import React, { useEffect } from "react";
import { ComponentProps } from "@stitches/react";
import Placeholder from "@tiptap/extension-placeholder";
import type { Editor } from "@tiptap/react";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import { useLinkModal } from "../../hooks/useLinkModal";
import { Icon, IconNames } from "../Common/Icons/LucideIcons";
import {
  EditorButton,
  EditorContainer,
  MenuBarContainer,
  Separator,
  StyledEditorContent,
} from "./__styles__/RichText";
import Wrapper from "./Wrapper";

interface Props {
  name: string;
  value?: string;
  onChange: (value: string) => void;
  label?: string | null;
  compactLabel?: boolean;
  helperText?: string | null;
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  labelSize?: ComponentProps<typeof Wrapper>["labelSize"];
  autoFocus?: boolean;
}

type MenuButton =
  | { separator: true }
  | {
      icon: string;
      onClick: () => void;
      active?: boolean;
      disabled?: boolean;
      separator?: false;
    };

const MenuBar = ({
  editor,
  onLinkClick,
}: {
  editor: Editor | null;
  onLinkClick: () => void;
}) => {
  if (!editor) return null;

  const buttons: MenuButton[] = [
    {
      icon: "bold",
      onClick: () => editor.chain().focus().toggleBold().run(),
      active: editor.isActive("bold"),
    },
    {
      icon: "italic",
      onClick: () => editor.chain().focus().toggleItalic().run(),
      active: editor.isActive("italic"),
    },
    {
      icon: "underline",
      onClick: () => editor.chain().focus().toggleUnderline().run(),
      active: editor.isActive("underline"),
    },
    { separator: true },
    {
      icon: "link",
      onClick: onLinkClick,
      active: editor.isActive("link"),
    },
    {
      icon: "list",
      onClick: () => editor.chain().focus().toggleBulletList().run(),
      active: editor.isActive("bulletList"),
    },
    {
      icon: "list-ordered",
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
      active: editor.isActive("orderedList"),
    },
    { separator: true },
    {
      icon: "indent-increase",
      onClick: () => editor.chain().focus().sinkListItem("listItem").run(),
      disabled: !editor.can().sinkListItem("listItem"),
    },
    {
      icon: "indent-decrease",
      onClick: () => editor.chain().focus().liftListItem("listItem").run(),
      disabled: !editor.can().liftListItem("listItem"),
    },
    { separator: true },
    {
      icon: "undo",
      onClick: () => editor.chain().focus().undo().run(),
      disabled: !editor.can().undo(),
    },
    {
      icon: "redo",
      onClick: () => editor.chain().focus().redo().run(),
      disabled: !editor.can().redo(),
    },
  ];

  return (
    <MenuBarContainer>
      {buttons.map((btn, index) => {
        if (btn.separator) {
          return (
            <Separator size="small" type="regular" key={`separator-${index}`}>
              |
            </Separator>
          );
        }
        const isDisabled = !editor.options.editable || (btn.disabled ?? false);

        return (
          <EditorButton
            key={btn.icon}
            type="button"
            aria-label={btn.icon}
            onClick={btn.onClick}
            active={btn.active ?? false}
            disabled={btn.disabled ?? false}
            editorDisabled={!editor.options.editable}
          >
            <Icon
              iconName={btn.icon as IconNames}
              color={isDisabled ? "contentDisabled" : "contentSecondary"}
              size="16px"
            />
          </EditorButton>
        );
      })}
    </MenuBarContainer>
  );
};

export const RichText = ({
  name,
  value = "",
  onChange,
  label = null,
  compactLabel,
  helperText,
  error,
  required,
  placeholder = "",
  disabled = false,
  readonly = false,
  labelSize,
  autoFocus,
}: Props) => {
  const editor = useEditor({
    editable: !disabled && !readonly,
    editorProps: {
      attributes: {
        id: name,
        "aria-labelledby": `${name}-label`,
        role: "textbox",
      },
    },
    autofocus: autoFocus,
    extensions: [
      // casting the options object "as any" because StarterKit does not
      // support the options in their configure arg type for the extensions
      // included in the kit
      StarterKit.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: true,
      } as any),
      Markdown.configure({
        breaks: true,
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      // @ts-expect-error - tiptap-markdown does not extend the Storage type
      // to include "markdown"
      const originalMarkdown = editor.storage.markdown.getMarkdown();
      const cleanedMarkdown = originalMarkdown.replace(/\\\n/g, "\n");
      onChange(cleanedMarkdown);
    },
  });

  const [showLinkModal] = useLinkModal(editor);

  // Sync editor content when value prop changes externally e.g. on form reset
  useEffect(() => {
    if (!editor) return;

    // @ts-expect-error - tiptap-markdown does not extend the Storage type
    // to include "markdown"
    const currentMarkdown = editor.storage.markdown.getMarkdown();
    const cleanedCurrent = currentMarkdown.replace(/\\\n/g, "\n");
    const cleanedValue = (value || "").replace(/\\\n/g, "\n");

    // Only update if the value has changed externally (not from user input)
    if (cleanedCurrent !== cleanedValue) {
      editor.commands.setContent(value || "");
    }
  }, [editor, value]);

  return (
    <Wrapper
      label={label}
      name={name}
      error={error}
      required={required}
      compactLabel={compactLabel}
      labelSize={labelSize}
      helperText={helperText}
      printValue={value?.toString() ?? ""}
    >
      <EditorContainer error={!!error} disabled={!editor?.options.editable}>
        <MenuBar editor={editor} onLinkClick={showLinkModal} />
        <StyledEditorContent editor={editor} />
      </EditorContainer>
    </Wrapper>
  );
};
